import { getServiceAnalyticUsages } from '@/modules/Service/services'
import {
    MonitoringQueries,
    ServiceMonitoringProps,
} from '@/modules/Service/types'
import { formatDateWithFormatter } from '@/utils/function'
import dayjs from 'dayjs'
import React, { useState } from 'react'
import { Loading } from 'vnetwork-ui'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import {
    Bandwidth,
    BusinessUsage as BusinessUsageType,
    Regions,
    Viewers,
    Volume,
} from '../types'
import BandwidthChart from './BandwidthChart'
import LocationViewer from './LocationViewer'
import LocationTable from './LocationTable'
import BusinessUsage from './BusinessUsage'
import Statistical from './Statistical'
import VolumeChart from './VolumeChart'
import Header from '../../../shared/Header'

import { QUERY_KEYS } from '@/constants/reactQueryKeys'

const today = dayjs()
const Monitoring: React.FC<ServiceMonitoringProps> = ({ service }) => {
    const [analyticQueries, setAnalyticQueries] = useState<MonitoringQueries>({
        range: 'this-month',
        gte: today.startOf('month').format('YYYY-MM-DD'),
        lte: today.endOf('month').format('YYYY-MM-DD'),
    })

    const {
        data,
        renderState: renderAnalyticsState,
        isPending,
        isLoading,
        isFetching,
        isSuccess,
    } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.SERVICES_MONITORING_CDN,
            service?.service_creatable_uuid,
            analyticQueries?.gte,
            analyticQueries?.lte,
        ],
        queryFn: () =>
            Promise.all([
                getServiceAnalyticUsages({
                    serviceID: service?.service_id,
                    params: {
                        gte: formatDateWithFormatter(
                            analyticQueries?.gte,
                            'YYYY-MM-DD'
                        ),
                        lte: formatDateWithFormatter(
                            analyticQueries?.lte,
                            'YYYY-MM-DD'
                        ),
                        type: '/bandwidth',
                    },
                }),
                getServiceAnalyticUsages({
                    serviceID: service?.service_id,
                    params: {
                        gte: formatDateWithFormatter(
                            analyticQueries?.gte,
                            'YYYY-MM-DD'
                        ),
                        lte: formatDateWithFormatter(
                            analyticQueries?.lte,
                            'YYYY-MM-DD'
                        ),
                        type: '/volume',
                    },
                }),
                getServiceAnalyticUsages({
                    serviceID: service?.service_id,
                    params: {
                        gte: formatDateWithFormatter(
                            analyticQueries?.gte,
                            'YYYY-MM-DD'
                        ),
                        lte: formatDateWithFormatter(
                            analyticQueries?.lte,
                            'YYYY-MM-DD'
                        ),
                        type: '/viewers',
                    },
                }),
                getServiceAnalyticUsages({
                    serviceID: service?.service_id,
                    params: {
                        gte: formatDateWithFormatter(
                            analyticQueries?.gte,
                            'YYYY-MM-DD'
                        ),
                        lte: formatDateWithFormatter(
                            analyticQueries?.lte,
                            'YYYY-MM-DD'
                        ),
                        type: '/business',
                    },
                }),
                getServiceAnalyticUsages({
                    serviceID: service?.service_id,
                    params: {
                        gte: formatDateWithFormatter(
                            analyticQueries?.gte,
                            'YYYY-MM-DD'
                        ),
                        lte: formatDateWithFormatter(
                            analyticQueries?.lte,
                            'YYYY-MM-DD'
                        ),
                        type: '/regions',
                    },
                }),
            ]),
    })

    const analyticsStateComponent = renderAnalyticsState()
    if (analyticsStateComponent) return analyticsStateComponent

    const isQueryReady = isSuccess && Array.isArray(data)

    const [current, volume, viewer, businessUsage, regionData] = isQueryReady
        ? (data as [
              { bandwidth: Bandwidth },
              { volume: Volume },
              { viewers: Viewers },
              { business: BusinessUsageType },
              { regions: Regions },
          ])
        : [
              {
                  bandwidth: {
                      data: [],
                      averageThroughput: 0,
                  },
              },
              { volume: { data: [] } },
              { viewers: [] },
              { business: [] },
              {
                  regions: {
                      local: 0,
                      global: 0,
                      cn: 0,
                      tw: 0,
                      th: 0,
                  },
              },
          ]

    // const [current, volume, viewer] = data as [{ bandwidth: Bandwidth }, { volume: Volume }, { viewers: Viewers }]

    return (
        <div className="">
            <Header
                analyticQueries={analyticQueries}
                setAnalyticQueries={setAnalyticQueries}
            />
            {isPending || isLoading || isFetching ? (
                <Loading.FadingDots width={40} height={40} />
            ) : (
                <div className="grid xs:grid-cols-1 md:grid-cols-2 gap-2">
                    <div className="xs:col-span-1 md:col-span-2">
                        <Statistical
                            bandwidth={current?.bandwidth}
                            volume={volume?.volume}
                            regions={regionData?.regions}
                        />
                    </div>
                    <div className="xs:col-span-1">
                        <BandwidthChart bandwidth={current?.bandwidth} />
                    </div>
                    <div className="xs:col-span-1">
                        <VolumeChart volume={volume?.volume} />
                    </div>
                    <div className="xs:col-span-1 md:col-span-2">
                        <LocationViewer viewers={viewer?.viewers} />
                    </div>
                    <div className="xs:col-span-1 md:col-span-2">
                        <LocationTable
                            viewers={viewer?.viewers}
                            reactQueryKey={`services.analytics.${service?.service_creatable_uuid}.traffic-location.${analyticQueries?.gte}.${analyticQueries?.lte}`}
                        />
                    </div>
                    <div className="xs:col-span-1 md:col-span-2">
                        <BusinessUsage
                            business={businessUsage?.business}
                            serviceID={service?.service_id}
                            gte={analyticQueries?.gte}
                            lte={analyticQueries?.lte}
                            serviceName={service?.service_name}
                            reactQueryKey={`services.analytics.${service?.service_creatable_uuid}.business-usage.${analyticQueries?.gte}.${analyticQueries?.lte}`}
                        />
                    </div>
                </div>
            )}
        </div>
    )
}

export default Monitoring
